import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { getMostRecentSaturday, getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";
import { JobCompletionCountResult } from "@app/operations/jobStatisticsResult";
import OperationsService from "@app/operations/operationsService";
import { JobCompletionRateChart } from "@app/operations/job-completion-rate/JobCompletionRateChart";
import { GRANULARITY_OPTIONS, JobCompletionGranularity } from "@app/operations/job-completion-rate/types";

interface JobCompletionRateProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

interface CompletionCountsQuery {
  endDate: string;
  granularity: JobCompletionGranularity;
  jobTypeId: number;
  startDate: string;
}

const operationsService = new OperationsService();
const CLIENT_SELECT_ERROR_TEXT = "Please select a Client";
const DATE_RANGE_ERROR_TEXT = "End Date must be on or after Start Date";
const UNKNOWN_JOB_TYPE_ID = 0;
const DEFAULT_JOB_TYPE: DropdownItem<number> = { key: 1002, name: "1002 - Custom Adequacy Region Summary" };

const getToday = (): string => new Date().toLocaleDateString("en-CA");

const JobCompletionRate: React.FC<JobCompletionRateProps> = ({ clients, clientId, setClientId }) => {
  const isHumana = clients.find((c) => c.clientId === clientId)?.name === "Humana";
  const [jobTypeItems, setJobTypeItems] = useState<DropdownItem<number>[]>([DEFAULT_JOB_TYPE]);
  const [selectedJobType, setSelectedJobType] = useState<DropdownItem<number>>(DEFAULT_JOB_TYPE);
  const [startDate, setStartDate] = useState<string>(isHumana ? getMostRecentSaturday() : getMostRecentWednesday());
  const [endDate, setEndDate] = useState<string>(getToday());
  const [granularity, setGranularity] = useState<JobCompletionGranularity>(JobCompletionGranularity.Hour);
  // Granularity is held with the results so editing the dropdown without searching cannot re-bucket
  // the data already on screen.
  const [chartData, setChartData] = useState<{ counts: JobCompletionCountResult[]; granularity: JobCompletionGranularity } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const activeCountsRequest = useRef<AbortController | null>(null);
  const hasLoadedInitialCounts = useRef<boolean>(false);

  // Takes the filters as an argument so the initial load can query the client-specific
  // start date in the same tick it is set, rather than waiting for the next render.
  const fetchCompletionCounts = async (queriedClientId: number, query: CompletionCountsQuery) => {
    if (query.endDate < query.startDate) {
      setError(DATE_RANGE_ERROR_TEXT);
      return;
    }

    activeCountsRequest.current?.abort();
    const abortController = new AbortController();
    activeCountsRequest.current = abortController;

    setLoading(true);
    setError("");
    try {
      const response = await operationsService.getJobCompletionCounts(queriedClientId, query.jobTypeId, query.startDate, query.endDate, query.granularity, abortController.signal);
      if (!abortController.signal.aborted) {
        setChartData({ counts: response, granularity: query.granularity });
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        setChartData(null);
        setError("Error retrieving job completion counts");
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const getJobCompletionCounts = () => {
    if (!clientId) {
      setError(CLIENT_SELECT_ERROR_TEXT);
      return;
    }

    fetchCompletionCounts(clientId, { endDate, granularity, jobTypeId: selectedJobType.key, startDate });
  };

  useEffect(() => {
    const abortController = new AbortController();
    operationsService
      .getJobTypes(abortController.signal)
      .then(
        (items) => {
          const options = items
            .filter((item) => item.id !== UNKNOWN_JOB_TYPE_ID)
            .map((item) => ({ key: item.id, name: `${item.id} - ${item.description}` }));
          setJobTypeItems(options);
          const matchingDefault = options.find((option) => option.key === DEFAULT_JOB_TYPE.key);
          if (matchingDefault) {
            setSelectedJobType(matchingDefault);
          }
        },
        () => setError("Error fetching Job Type options")
      );

    return () => abortController.abort();
  }, []);

  // The parent resolves clientId from the active tab asynchronously, so the first load waits
  // for it instead of firing on mount. Runs once so a later client change won't discard edited filters.
  useEffect(() => {
    if (hasLoadedInitialCounts.current || !clientId) {
      return;
    }

    hasLoadedInitialCounts.current = true;
    const clientStartDate = isHumana ? getMostRecentSaturday() : getMostRecentWednesday();
    setStartDate(clientStartDate);
    fetchCompletionCounts(clientId, { endDate, granularity, jobTypeId: selectedJobType.key, startDate: clientStartDate });
  }, [clientId, isHumana]);

  useEffect(() => () => activeCountsRequest.current?.abort(), []);

  return (
    <div className="job-completion-rate">
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            id="jobCompletionClientSelect"
            label="Client"
            value={clientId ?? ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="job-completion-rate__client-select"
            required
            disabled={clients.length === 0}
            isValid={clientId !== null}
            errorText={CLIENT_SELECT_ERROR_TEXT}
            positionDescriptionTextAbsolutely={true}
          >
            {clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col-3 px-1 job-completion-rate__job-type">
          <SingleSelectDropdown
            customWidth={true}
            items={jobTypeItems}
            label="Job Type"
            onSelectionChanged={setSelectedJobType}
            selectedItem={selectedJobType}
            showTooltipOnOverflow={true}
            testId="job-completion-rate__job-type"
          />
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Start Date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            testId="job-completion-rate__start-date-input"
          />
        </div>
        <div className="col px-1">
          <QESDateInput
            label="End Date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            testId="job-completion-rate__end-date-input"
          />
        </div>
        <div className="col px-1">
          <QESSelect
            label="Granularity"
            value={granularity}
            onChange={(event) => setGranularity(event.target.value as JobCompletionGranularity)}
            testId="job-completion-rate__granularity-select"
          >
            {GRANULARITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col d-flex align-items-end ps-1">
          <QESButton onClick={getJobCompletionCounts} qesType="primary" qesTestId="job-completion-rate__search">
            Search
          </QESButton>
        </div>
      </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div data-testid="job-completion-rate__error">{error}</div>}
        {!loading && chartData && <JobCompletionRateChart data={chartData.counts} granularity={chartData.granularity} />}
      </div>
    </div>
  );
};

export default JobCompletionRate;
